// Weight: the trend, never the morning. The seven-day average, how it has moved, a line you can
// scrub to read any day, and a field to log a reading by hand until the Shortcut does it.
// Never red, never a headline: the number sits at stat size, not hero size.
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { daily, avg7, fmtW, fmtDelta, parseW, type WeighIn } from "../lib/weight";
import { useMusette } from "../lib/store";
import { dateLabel, todayIso, addDays, daysBetween } from "../lib/dates";
import { Head } from "../components/Head";
import { useToast } from "../components/Sheet";

const RANGES = [["4 weeks", 28], ["8 weeks", 56], ["6 months", 182]] as const;

export function Weight() {
  const [w, setW] = useState<WeighIn[] | null>(null);
  const [range, setRange] = useState(56);
  const [kg, setKg] = useState("");
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  const { show, node: toast } = useToast();
  const unit = useMusette().settings.weightUnit ?? "st";
  const load = () => fetch("/api/weight", { credentials: "same-origin" }).then((r) => (r.ok ? r.json() : { weighins: [] })).then((b) => setW(b.weighins ?? [])).catch(() => setW([]));
  // Back to the app after a weigh-in: read again, so a reading the Shortcut just sent shows at once.
  useEffect(() => { load(); const on = () => { if (document.visibilityState === "visible") load(); }; document.addEventListener("visibilitychange", on); return () => document.removeEventListener("visibilitychange", on); }, []);

  const today = todayIso();
  const days = daily(w ?? []);
  const now = avg7(days, today);
  const then = avg7(days, addDays(today, -28));
  const last = days[days.length - 1];
  const from = addDays(today, -range);
  const recent = days.filter((x) => x.d >= from);
  const lo = recent.length ? Math.min(...recent.map((x) => x.kg)) - 0.3 : 0, hi = recent.length ? Math.max(...recent.map((x) => x.kg)) + 0.3 : 1;
  const W = 358, H = 140;
  const pts = useMemo(() => recent.map((x) => ({ ...x, x: (daysBetween(from, x.d) / range) * W, y: H - 12 - ((x.kg - lo) / (hi - lo || 1)) * (H - 28) })), [w, range]);
  const avgPts = pts.map((p) => { const a = avg7(days, p.d); return a == null ? null : { x: p.x, y: H - 12 - ((a - lo) / (hi - lo || 1)) * (H - 28) }; }).filter(Boolean) as { x: number; y: number }[];
  const sel = at != null ? pts[at] : null;

  const scrub = (e: PointerEvent) => {
    if (!pts.length || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    let best = 0; pts.forEach((p, i) => { if (Math.abs(p.x - x) < Math.abs(pts[best].x - x)) best = i; });
    setAt(best);
  };
  const log = async () => {
    const v = parseW(kg, unit);
    if (v == null || v < 30 || v > 250) { show(unit === "st" ? "Type it as stones and pounds: 14 11.8" : "That doesn't look like a weight in kg."); return; }
    setBusy(true);
    const r = await fetch("/api/weight", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ d: today, kg: v }) }).catch(() => null);
    setBusy(false);
    if (!r || !r.ok) { show(r?.status === 401 ? "Sign in first, from the menu." : "It didn't save. Try again in a moment."); return; }
    setKg(""); show(`${fmtW(v, unit)} logged for today.`); load();
  };

  return (
    <main class="screen signal">
      <Head right="Weight" />
      <section class="stack" style="margin-top: var(--space-3)">
        <a href="/coach" class="label back">← Coach</a>

        <div class="card">
          <div class="row-between"><span class="label ink">Seven-day average</span><span class="label">From the scales</span></div>
          {w === null ? <span class="soft">Reading your weigh-ins.</span> : !days.length ? (
            <span class="soft">No weigh-ins yet. Once FITINDEX readings arrive through the Shortcut, the seven-day average and the trend fill in here.</span>
          ) : (
            <>
              <div class="weight-top">
                <span class="stat-n big">{fmtW(now ?? last.kg, unit)}</span>
                <span class="soft">{now != null ? (then != null ? `${fmtDelta(now - then, unit)} ${now <= then ? "lighter" : "heavier"} than four weeks ago` : "Four weeks of readings give the trend") : `On ${dateLabel(last.d)}. The average needs three days.`}</span>
              </div>
              <div class="segmented three" role="tablist">
                {RANGES.map(([l, n]) => <button type="button" role="tab" aria-selected={range === n} onClick={() => { setRange(n); setAt(null); }}>{l}</button>)}
              </div>
              {pts.length > 1 ? (
                <div class="weight-chart">
                  <div class="row-between"><span class="label ink">{sel ? dateLabel(sel.d) : `${recent.length} readings`}</span><span class="label">{sel ? `${fmtW(sel.kg, unit)}, measured` : "Drag to read a day"}</span></div>
                  <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label={`Weight over ${range} days`}
                    onPointerDown={scrub as any} onPointerMove={(e) => { if ((e as PointerEvent).buttons || (e as PointerEvent).pointerType === "mouse") scrub(e as any); }} onPointerLeave={() => setAt(null)} style="touch-action: pan-y">
                    {pts.map((p) => <circle cx={p.x} cy={p.y} r="2.5" class="w-dot" />)}
                    {avgPts.length > 1 && <polyline points={avgPts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")} class="w-line" fill="none" vector-effect="non-scaling-stroke" />}
                    {sel && <><line x1={sel.x} x2={sel.x} y1="0" y2={H} class="w-cursor" vector-effect="non-scaling-stroke" /><circle cx={sel.x} cy={sel.y} r="5" class="w-sel" /></>}
                  </svg>
                  <div class="row-between"><span class="label">{dateLabel(from)}</span><span class="label">{fmtW(lo + 0.3, unit)} to {fmtW(hi - 0.3, unit)}</span><span class="label">Today</span></div>
                  <span class="label">Dots are mornings; the line is the seven-day average.</span>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div class="card">
          <span class="label ink">Log today</span>
          <div class="composer">
            <input inputMode="decimal" placeholder={unit === "st" ? "Stones and pounds: 14 11.8" : "kg, from the scales"} value={kg} onInput={(e) => setKg((e.target as HTMLInputElement).value)} aria-label={unit === "st" ? "Today's weight in stones and pounds" : "Today's weight in kilograms"} />
            <button type="button" class="send" disabled={!kg || busy} onClick={log} aria-label="Log it">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            </button>
          </div>
          <span class="soft">Until the Shortcut sends it from Apple Health each morning. Same day twice keeps the later one.</span>
        </div>

        {last && <div class="card row-between"><span class="label ink">Last weigh-in</span><span class="label">{fmtW(last.kg, unit)}{w?.[w.length - 1]?.fat ? ` · ${w[w.length - 1].fat}% body fat` : ""} · {dateLabel(last.d)}</span></div>}
      </section>
      <div class="dock-wrap">{toast}</div>
    </main>
  );
}
